import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Clock, CheckCircle, XCircle, Plus, X, Copy, Search, BarChart3, ChevronDown, MoreVertical, AlertCircle } from 'lucide-react';
import apiService, { ReconnectRequest, GroupedReconnectRequests } from '../services/api';

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const Reconnects: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [requests, setRequests] = useState<ReconnectRequest[]>([]);
  const [groupedRequests, setGroupedRequests] = useState<GroupedReconnectRequests[]>([]);
  const [statistics, setStatistics] = useState({
    total_requests: 0,
    unique_workspaces: 0,
    completed_requests: 0,
    pending_requests: 0,
    processing_requests: 0,
    failed_requests: 0,
    cancelled_requests: 0,
    disabled_requests: 0,
    edu_suspended_requests: 0,
    tfa_sms_requests: 0,
    incorrect_password_requests: 0,
    captcha_requests: 0,
    google_not_found_requests: 0,
    spam_requests: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10, // Reduced from 15 to 10 for better performance
    total: 0,
    totalPages: 0,
  });
  
  // Separate pagination for workspace groups
  const [workspacePagination, setWorkspacePagination] = useState({
    page: 1,
    limit: 25, // 25 workspace IDs per page
    total: 0,
    totalPages: 0,
  });
  
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // Debounce search
  const [dateFilter, setDateFilter] = useState('24h');
  const [groupBy, setGroupBy] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    priority: 'normal' as 'urgent' | 'high' | 'normal' | 'low',
    id: '',
    workspace_id: '',
    password: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [copiedPassword, setCopiedPassword] = useState<string | null>(null);

  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Dropdown state
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());

  // Process request state
  const [processingRequests, setProcessingRequests] = useState<Set<string>>(new Set());
  const [processSuccess, setProcessSuccess] = useState<string | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);

  // Cancel token for aborting requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch statistics separately
  useEffect(() => {
    const fetchStatistics = async () => {
      setStatsLoading(true);
      try {
        const response = await apiService.getReconnectStatistics({
          status: statusFilter,
          priority: priorityFilter,
          dateFilter: dateFilter,
          search: debouncedSearchQuery
        });
        if (response.statistics) {
          setStatistics(response.statistics);
        }
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    // Fetch statistics immediately
    fetchStatistics();
    
    // Set up interval for periodic refresh
    const interval = setInterval(fetchStatistics, 60000);

    return () => clearInterval(interval);
  }, [statusFilter, priorityFilter, dateFilter, debouncedSearchQuery]);

  useEffect(() => {
    fetchRequests();
    return () => {
      // Cleanup: abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [pagination.page, workspacePagination.page, statusFilter, priorityFilter, debouncedSearchQuery, dateFilter, groupBy, sortBy, sortOrder]);

  const fetchRequests = async (isRetry = false) => {
    try {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      setError(null);
      
      // Use different pagination based on grouping mode
      const currentPagination = groupBy === 'workspace_id' ? workspacePagination : pagination;
      
      const response = await apiService.getReconnectRequests({
        page: currentPagination.page,
        limit: currentPagination.limit,
        status: statusFilter,
        priority: priorityFilter,
        search: debouncedSearchQuery,
        dateFilter: dateFilter,
        groupBy: groupBy,
        sortBy,
        sortOrder,
      });
      
      setRequests(response.data);
      setGroupedRequests(response.grouped_data || []);
      // Don't update statistics here - they come from separate endpoint
      
      // Update the appropriate pagination state
      if (groupBy === 'workspace_id') {
        setWorkspacePagination(response.pagination);
      } else {
        setPagination(response.pagination);
      }
      
      // Reset retry count on success
      setRetryCount(0);
    } catch (err: any) {
      // Don't show error for aborted requests
      if (err.name === 'AbortError') {
        return;
      }
      
      console.error('Failed to fetch reconnect requests:', err);
      
      // Implement retry logic for network errors
      if (!isRetry && retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchRequests(true);
        }, 1000 * (retryCount + 1)); // Exponential backoff
      } else {
        // Check for specific error messages
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else if (err.message) {
          setError(err.message);
        } else {
          setError('Failed to fetch reconnect requests. Please check your connection and try again.');
        }
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleRefresh = useCallback(() => {
    setRetryCount(0);
    fetchRequests();
  }, []);

  const handleCreateRequest = async () => {
    if (!createForm.email) {
      setCreateError('Email is required');
      return;
    }
    
    try {
      setCreateLoading(true);
      setCreateError(null);
      
      const requestData: any = {
        email: createForm.email,
        priority: createForm.priority,
      };
      
      if (createForm.id) {
        requestData.id = createForm.id;
      }
      
      if (createForm.workspace_id && createForm.password) {
        requestData.workspace_id = createForm.workspace_id;
        requestData.password = createForm.password;
      }
      
      await apiService.createReconnectRequest(requestData);
      
      // Reset form and close modal
      setCreateForm({ email: '', priority: 'normal', id: '', workspace_id: '', password: '' });
      setShowCreateModal(false);
      
      // Refresh the list
      fetchRequests();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create reconnect request');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleProcessRequest = async (requestId: string) => {
    // Clear any previous notifications
    setProcessSuccess(null);
    setProcessError(null);
    
    // Add to processing set for loading state
    setProcessingRequests(prev => new Set([...Array.from(prev), requestId]));
    
    // Close dropdown
    setOpenDropdowns(new Set());
    
    try {
      // Optimistically update the request status to "processing" in the UI
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: 'processing' }
            : request
        )
      );
      
      // Also update grouped requests if applicable
      setGroupedRequests(prevGrouped =>
        prevGrouped.map(group => ({
          ...group,
          requests: group.requests.map(request => 
            request.id === requestId 
              ? { ...request, status: 'processing' }
              : request
          )
        }))
      );
      
      const result = await apiService.processReconnectRequest(requestId);
      
      // Show success notificationc
      setProcessSuccess(`✓ Request processed successfully! Sent to reconnect server for ${result.data?.email || 'reconnection'}.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setProcessSuccess(null), 5000);
      
      console.log('✓ Request processed successfully:', result.message);
      
      // Refresh requests to get the latest data from server
      fetchRequests();
      
    } catch (err: any) {
      console.error('Failed to process request:', err);
      
      // Show error notification
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process reconnect request';
      setProcessError(errorMessage);
      
      // Clear error message after 8 seconds
      setTimeout(() => setProcessError(null), 8000);
      
      // Revert optimistic update by fetching fresh data
      fetchRequests();
      
    } finally {
      // Remove from processing set
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      await apiService.updateReconnectRequestStatus(requestId, 'cancelled');
      fetchRequests();
    } catch (err) {
      console.error('Failed to cancel request:', err);
    }
  };

  const copyToClipboard = async (text: string, requestId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPassword(requestId);
      setTimeout(() => setCopiedPassword(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
      case 'disabled':
      case 'edu_suspended':
      case '2fa_sms':
      case 'incorrect_password':
      case 'captcha':
      case 'google_not_found':
      case 'spam':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-500/10 text-green-500`;
      case 'failed':
      case 'disabled':
      case 'edu_suspended':
      case '2fa_sms':
      case 'incorrect_password':
      case 'captcha':
      case 'google_not_found':
      case 'spam':
        return `${baseClasses} bg-destructive/10 text-destructive`;
      case 'processing':
        return `${baseClasses} bg-blue-500/10 text-blue-500`;
      case 'cancelled':
        return `${baseClasses} bg-gray-500/10 text-gray-500`;
      default:
        return `${baseClasses} bg-amber-500/10 text-amber-500`;
    }
  };

  const formatStatusDisplay = (status: string) => {
    switch (status) {
      case 'edu_suspended':
        return 'Edu Suspended';
      case '2fa_sms':
        return '2FA SMS';
      case 'incorrect_password':
        return 'Incorrect Password';
      case 'google_not_found':
        return 'Google Not Found';
      case 'spam':
        return 'Spam';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const baseClasses = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
    switch (priority) {
      case 'urgent':
        return `${baseClasses} bg-destructive/10 text-destructive`;
      case 'high':
        return `${baseClasses} bg-amber-500/10 text-amber-500`;
      case 'low':
        return `${baseClasses} bg-muted text-muted-foreground`;
      default:
        return `${baseClasses} bg-primary/10 text-primary`;
    }
  };

  const toggleDropdown = (requestId: string) => {
    const newOpenDropdowns = new Set(openDropdowns);
    if (newOpenDropdowns.has(requestId)) {
      newOpenDropdowns.delete(requestId);
    } else {
      newOpenDropdowns.clear(); // Close all other dropdowns
      newOpenDropdowns.add(requestId);
    }
    setOpenDropdowns(newOpenDropdowns);
  };

  const handleChangeStatus = (requestId: string, currentStatus: string) => {
    setSelectedRequestId(requestId);
    setSelectedStatus(currentStatus);
    setShowStatusModal(true);
    setOpenDropdowns(new Set()); // Close all dropdowns
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequestId || !selectedStatus) return;
    
    try {
      setStatusLoading(true);
      setStatusError(null);
      await apiService.updateReconnectRequestStatus(selectedRequestId, selectedStatus as any);
      setShowStatusModal(false);
      setSelectedRequestId(null);
      setSelectedStatus('');
      fetchRequests();
    } catch (err: any) {
      setStatusError(err.message || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdowns(new Set());
    };
    
    if (openDropdowns.size > 0) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdowns]);

  return (
    <div className="space-y-6 animate-fade-in pt-12 lg:pt-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reconnect Requests</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage domain reconnection requests from users
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>New Request</span>
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">Total</span>
          </div>
          <p className="text-2xl font-bold">
            {statsLoading ? <span className="text-sm">Loading...</span> : statistics.total_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm text-muted-foreground">Completed</span>
          </div>
          <p className="text-2xl font-bold text-green-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.completed_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-muted-foreground">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.pending_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-muted-foreground">Processing</span>
          </div>
          <p className="text-2xl font-bold text-blue-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.processing_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-muted-foreground">Failed</span>
          </div>
          <p className="text-2xl font-bold text-destructive">
            {statsLoading ? <span className="text-sm">—</span> : statistics.failed_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-muted-foreground">Cancelled</span>
          </div>
          <p className="text-2xl font-bold text-gray-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.cancelled_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-muted-foreground">Disabled</span>
          </div>
          <p className="text-2xl font-bold text-red-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.disabled_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-muted-foreground">2FA SMS</span>
          </div>
          <p className="text-2xl font-bold text-yellow-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.tfa_sms_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-pink-500" />
            <span className="text-sm text-muted-foreground">Wrong Password</span>
          </div>
          <p className="text-2xl font-bold text-pink-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.incorrect_password_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-purple-500" />
            <span className="text-sm text-muted-foreground">Captcha</span>
          </div>
          <p className="text-2xl font-bold text-purple-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.captcha_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-muted-foreground">Google Not Found</span>
          </div>
          <p className="text-2xl font-bold text-red-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.google_not_found_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-muted-foreground">Spam</span>
          </div>
          <p className="text-2xl font-bold text-orange-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.spam_requests}
          </p>
        </div>
        <div className="card-modern p-4">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4 text-indigo-500" />
            <span className="text-sm text-muted-foreground">Workspaces</span>
          </div>
          <p className="text-2xl font-bold text-indigo-500">
            {statsLoading ? <span className="text-sm">—</span> : statistics.unique_workspaces}
          </p>
        </div>
      </div>

      {/* Process Notifications */}
      {processSuccess && (
        <div className="card-modern p-4 border-l-4 border-green-500 bg-green-50/50">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-sm text-green-700 font-medium">{processSuccess}</p>
          </div>
        </div>
      )}
      
      {processError && (
        <div className="card-modern p-4 border-l-4 border-red-500 bg-red-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <p className="text-sm text-red-700 font-medium">{processError}</p>
            </div>
            <button
              onClick={() => setProcessError(null)}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <input
              type="text"
              placeholder="Search by email address..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination({ ...pagination, page: 1 });
                setWorkspacePagination({ ...workspacePagination, page: 1 });
              }}
              className="input-modern h-10 pl-10 pr-4 w-full text-sm"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <select
            className="input-modern h-10 px-3 text-sm min-w-[130px]"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
              setWorkspacePagination({ ...workspacePagination, page: 1 });
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="disabled">Disabled</option>
            <option value="2fa_sms">2FA SMS</option>
            <option value="incorrect_password">Incorrect Password</option>
            <option value="captcha">Captcha</option>
            <option value="google_not_found">Google Not Found</option>
            <option value="spam">Spam</option>
          </select>

          <select
            className="input-modern h-10 px-3 text-sm min-w-[130px]"
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
              setWorkspacePagination({ ...workspacePagination, page: 1 });
            }}
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>

          <select
            className="input-modern h-10 px-3 text-sm min-w-[130px]"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
              setWorkspacePagination({ ...workspacePagination, page: 1 });
            }}
          >
            <option value="">All Time</option>
            <option value="10m">Last 10 Minutes</option>
            <option value="1h">Last 1 Hour</option>
            <option value="3h">Last 3 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="1w">Last 7 Days</option>
          </select>

          <select
            className="input-modern h-10 px-3 text-sm min-w-[130px]"
            value={groupBy}
            onChange={(e) => {
              setGroupBy(e.target.value);
              setPagination({ ...pagination, page: 1 });
              setWorkspacePagination({ ...workspacePagination, page: 1 });
            }}
          >
            <option value="">No Grouping</option>
            <option value="workspace_id">Group by Workspace</option>
          </select>

          <select
            className="input-modern h-10 px-3 text-sm min-w-[100px]"
            value={pagination.limit}
            onChange={(e) => {
              setPagination({ ...pagination, limit: parseInt(e.target.value), page: 1 });
            }}
          >
            <option value="5">5 / page</option>
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
          </select>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-card">
        {loading ? (
          <div className="flex flex-col justify-center items-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading reconnect requests...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-destructive text-center max-w-md">{error}</p>
            <button
              onClick={handleRefresh}
              className="btn-primary px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Retry</span>
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No reconnect requests found</p>
          </div>
        ) : groupBy === 'workspace_id' && groupedRequests.length > 0 ? (
          // Grouped view by workspace ID
          <div className="divide-y divide-border">
            {groupedRequests.map((group, groupIndex) => (
              <div key={group.workspace_id} className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center space-x-2">
                    <span>Workspace ID: {group.workspace_id}</span>
                    <span className="text-sm font-normal text-muted-foreground">
                      ({group.count} request{group.count !== 1 ? 's' : ''})
                    </span>
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] divide-y divide-border">
                    <thead className="bg-muted/30">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[200px]">
                          Email
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[100px]">
                          Password
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[90px]">
                          Priority
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[100px]">
                          Status
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[70px]">
                          Attempts
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[120px]">
                          Created
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold w-[140px]">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {group.requests.map((request) => (
                        <tr key={request.id} className="hover:bg-muted/20">
                          <td className="px-4 py-3 text-sm w-[200px]">
                            {request.email}
                          </td>
                          <td className="px-4 py-3 text-sm w-[100px]">
                            <button
                              onClick={() => copyToClipboard(request.password || '', request.id)}
                              className="flex items-center space-x-1 text-muted-foreground hover:text-foreground transition-colors"
                              title="Click to copy password"
                            >
                              <span className="font-mono text-xs">••••••••</span>
                              {copiedPassword === request.id ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm w-[90px]">
                            <span className={getPriorityBadge(request.priority)}>
                              {request.priority}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm w-[100px]">
                            <div className="flex items-center space-x-1">
                              {getStatusIcon(request.status)}
                              <span className={getStatusBadge(request.status)}>
                                {formatStatusDisplay(request.status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-center w-[70px]">
                            {request.attempts}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground w-[120px]">
                            {formatDate(request.created_at)}
                          </td>
                          <td className="px-4 py-3 text-sm w-[140px]">
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDropdown(request.id);
                                }}
                                className="flex items-center space-x-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted transition-colors"
                              >
                                <MoreVertical className="h-3 w-3" />
                                <span>Actions</span>
                                <ChevronDown className="h-2 w-2" />
                              </button>
                              
                              {openDropdowns.has(request.id) && (
                                <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                                  <div className="py-1">
                                    <button
                                      onClick={() => {
                                        handleProcessRequest(request.id);
                                        setOpenDropdowns(new Set());
                                      }}
                                      disabled={processingRequests.has(request.id)}
                                      className="w-full px-3 py-2 text-left text-xs text-foreground hover:bg-muted transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <RefreshCw className={`h-3 w-3 ${processingRequests.has(request.id) ? 'animate-spin' : ''}`} />
                                      <span>{processingRequests.has(request.id) ? 'Processing...' : 'Process'}</span>
                                    </button>
                                    <button
                                      onClick={() => handleChangeStatus(request.id, request.status)}
                                      className="w-full px-3 py-2 text-left text-xs text-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                                    >
                                      <XCircle className="h-3 w-3" />
                                      <span>Change Status</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                              
                              {request.error_message && (
                                <div className="mt-1">
                                  <span className="text-destructive text-xs" title={request.error_message}>
                                    Error
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Regular table view
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1400px] divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[300px]">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[100px]">
                    Password
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[220px]">
                    Workspace ID
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[90px]">
                    Priority
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[110px]">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[80px]">
                    Attempts
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[150px]">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-sm font-semibold w-[150px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td className="px-6 py-4 text-sm w-[300px] break-all">
                      {request.email}
                    </td>
                    <td className="px-6 py-4 text-sm w-[100px]">
                      <button
                        onClick={() => copyToClipboard(request.password || '', request.id)}
                        className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
                        title="Click to copy password"
                      >
                        <span className="font-mono">••••••••</span>
                        {copiedPassword === request.id ? (
                          <CheckCircle className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium w-[220px] break-all">
                      {request.workspace_id}
                    </td>
                    <td className="px-6 py-4 text-sm w-[90px]">
                      <span className={getPriorityBadge(request.priority)}>
                        {request.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm w-[110px]">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(request.status)}
                        <span className={getStatusBadge(request.status)}>
                          {formatStatusDisplay(request.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-center w-[80px]">
                      {request.attempts}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground w-[150px]">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-6 py-4 text-sm w-[150px]">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(request.id);
                          }}
                          className="flex items-center space-x-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted transition-colors"
                        >
                          <MoreVertical className="h-3 w-3" />
                          <span>Actions</span>
                          <ChevronDown className="h-2 w-2" />
                        </button>
                        
                        {openDropdowns.has(request.id) && (
                          <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleProcessRequest(request.id);
                                  setOpenDropdowns(new Set());
                                }}
                                disabled={processingRequests.has(request.id)}
                                className="w-full px-3 py-2 text-left text-xs text-foreground hover:bg-muted transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <RefreshCw className={`h-3 w-3 ${processingRequests.has(request.id) ? 'animate-spin' : ''}`} />
                                <span>{processingRequests.has(request.id) ? 'Processing...' : 'Process'}</span>
                              </button>
                              <button
                                onClick={() => handleChangeStatus(request.id, request.status)}
                                className="w-full px-3 py-2 text-left text-xs text-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                              >
                                <XCircle className="h-3 w-3" />
                                <span>Change Status</span>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {request.error_message && (
                          <div className="mt-1">
                            <span className="text-destructive text-xs" title={request.error_message}>
                              Error
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && (
        (groupBy === 'workspace_id' ? workspacePagination.totalPages > 1 : pagination.totalPages > 1)
      ) && (
        <div className="flex items-center justify-center">
          <nav className="inline-flex -space-x-px rounded-lg shadow-sm">
            <button
              onClick={() => {
                if (groupBy === 'workspace_id') {
                  setWorkspacePagination({ ...workspacePagination, page: workspacePagination.page - 1 });
                } else {
                  setPagination({ ...pagination, page: pagination.page - 1 });
                }
              }}
              disabled={groupBy === 'workspace_id' ? workspacePagination.page === 1 : pagination.page === 1}
              className="relative inline-flex items-center rounded-l-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold border border-border bg-card">
              {groupBy === 'workspace_id' 
                ? `Page ${workspacePagination.page} of ${workspacePagination.totalPages} (${workspacePagination.total} workspaces)`
                : `Page ${pagination.page} of ${pagination.totalPages}`
              }
            </span>
            <button
              onClick={() => {
                if (groupBy === 'workspace_id') {
                  setWorkspacePagination({ ...workspacePagination, page: workspacePagination.page + 1 });
                } else {
                  setPagination({ ...pagination, page: pagination.page + 1 });
                }
              }}
              disabled={groupBy === 'workspace_id' ? workspacePagination.page === workspacePagination.totalPages : pagination.page === pagination.totalPages}
              className="relative inline-flex items-center rounded-r-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="card-modern max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Create Reconnect Request</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {createError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {createError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Priority
                </label>
                <select
                  value={createForm.priority}
                  onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value as any })}
                  className="input-modern h-10 w-full px-3 text-sm"
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  External ID (Optional)
                </label>
                <input
                  type="text"
                  value={createForm.id}
                  onChange={(e) => setCreateForm({ ...createForm, id: e.target.value })}
                  className="input-modern h-10 w-full px-3 text-sm"
                  placeholder="custom-id-123"
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Optional: Provide workspace ID and password to skip domain lookup
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  Platform will default to "Instantly" for all requests
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Workspace ID
                    </label>
                    <input
                      type="text"
                      value={createForm.workspace_id}
                      onChange={(e) => setCreateForm({ ...createForm, workspace_id: e.target.value })}
                      className="input-modern h-10 w-full px-3 text-sm"
                      placeholder="workspace-123"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Password
                    </label>
                    <input
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      className="input-modern h-10 w-full px-3 text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRequest}
                disabled={createLoading}
                className="btn-primary px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {createLoading ? 'Creating...' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="card-modern max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Change Status</h2>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusError(null);
                  setSelectedRequestId(null);
                  setSelectedStatus('');
                }}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {statusError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {statusError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  New Status <span className="text-destructive">*</span>
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="input-modern h-10 w-full px-3 text-sm"
                >
                  <option value="">Select a status...</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="disabled">Disabled</option>
                  <option value="2fa_sms">2FA SMS</option>
                  <option value="incorrect_password">Incorrect Password</option>
                  <option value="captcha">Captcha</option>
                  <option value="google_not_found">Google Not Found</option>
                  <option value="spam">Spam</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setStatusError(null);
                  setSelectedRequestId(null);
                  setSelectedStatus('');
                }}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={statusLoading || !selectedStatus}
                className="btn-primary px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {statusLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 